import { Building2, ChevronLeft, ChevronRight, Cloud, Folder, FolderCheck, Library, Loader2, Trash2, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, endpoints, type DriveListing, type DriveLocations } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { newId, useSettings } from '../../store/settings';
import { buttonClass, ghostButtonClass } from './controls';

interface Crumb {
  id: string | undefined;
  name: string;
}

/** Where browsing starts: the user's own OneDrive, a shared folder or a SharePoint library. */
interface Start {
  drive?: string;
  /** Folder to open first; unset = the drive's root. */
  folderId?: string;
  /** Label of the starting point, e.g. "OneDrive" or "Marketing / Documents". */
  name: string;
}

/**
 * Browse OneDrive – and, for work/school accounts, SharePoint libraries and folders shared
 * with you – and add folders whose photos/videos join the slideshow.
 */
export function OneDrivePicker() {
  const { config, user, microsoft, refreshMicrosoft } = useSession();
  const folders = useSettings((s) => s.media.cloudFolders);
  const update = useSettings((s) => s.update);
  const [step, setStep] = useState<'idle' | 'locations' | Start>('idle');
  // Where the browser is, held here so a re-render of the panel never resets it.
  const [path, setPath] = useState<Crumb[]>([]);
  const open = useCallback((s: Start) => {
    setPath([{ id: s.folderId, name: s.name }]);
    setStep(s);
  }, []);

  if (!config?.features.microsoft) return null;

  const remove = (id: string) => update('media', { cloudFolders: folders.filter((f) => f.id !== id) });
  const work = Boolean(microsoft?.work);
  const workAccess = Boolean(microsoft?.workAccess);

  return (
    // Sits inside the Microsoft card on Settings → Media → Cloud storage, which carries the heading.
    <div className="space-y-3">
      <p className="muted text-sm">
        {work
          ? 'Photos and videos from your OneDrive, SharePoint libraries and folders shared with you.'
          : 'Photos and videos from folders in your OneDrive.'}
      </p>
      {!user ? (
        <p className="muted text-sm">Sign in (Settings → Account) to connect OneDrive.</p>
      ) : !microsoft?.connected ? (
        <button className={buttonClass} onClick={() => startMicrosoft('link')}>
          <Cloud className="h-4 w-4" aria-hidden /> Connect Microsoft account
        </button>
      ) : (
        <>
          <p className="muted text-xs">Connected as {microsoft.email ?? 'your Microsoft account'}.</p>
          {folders.length > 0 && (
            <ul className="space-y-2">
              {folders.map((f) => {
                const deep = f.includeSubfolders !== false;
                return (
                  <li key={f.id} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
                    <div className="flex items-center gap-3">
                      <FolderCheck className="h-4 w-4 shrink-0 text-[var(--accent)]" aria-hidden />
                      <span className="min-w-0 flex-1 truncate" title={f.name}>
                        {f.name}
                      </span>
                      <button onClick={() => remove(f.id)} aria-label={`Remove ${f.name}`} className="opacity-60 hover:opacity-100">
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                    <label className="muted mt-1.5 flex cursor-pointer items-center gap-2 pl-7 text-xs">
                      <input
                        type="checkbox"
                        checked={deep}
                        onChange={() =>
                          update('media', {
                            cloudFolders: folders.map((x) => (x.id === f.id ? { ...x, includeSubfolders: !deep } : x)),
                          })
                        }
                        className="accent-[var(--accent)]"
                      />
                      Include all sub-folders
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {step === 'locations' ? (
            <Locations onOpen={open} onCancel={() => setStep('idle')} />
          ) : step !== 'idle' ? (
            <Browser
              start={step}
              path={path.length ? path : [{ id: step.folderId, name: step.name }]}
              setPath={setPath}
              onPick={(folderId, name) => {
                if (!folders.some((f) => f.folderId === folderId && (f.driveId ?? null) === (step.drive ?? null))) {
                  update('media', {
                    cloudFolders: [
                      ...folders,
                      { id: newId(), provider: 'onedrive', folderId, name, includeSubfolders: true, ...(step.drive ? { driveId: step.drive } : {}) },
                    ],
                  });
                }
                setStep('idle');
              }}
              onBack={workAccess ? () => setStep('locations') : undefined}
              onCancel={() => setStep('idle')}
              onDisconnected={() => void refreshMicrosoft()}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              <button className={ghostButtonClass} onClick={() => (workAccess ? setStep('locations') : open({ name: 'OneDrive' }))}>
                <Folder className="h-4 w-4" aria-hidden /> Choose a folder
              </button>
              {work && !workAccess && (
                <button className={ghostButtonClass} onClick={() => startMicrosoft('link', undefined, true)}>
                  <Building2 className="h-4 w-4" aria-hidden /> Allow SharePoint and shared folders
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const rowClass = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-[var(--border)]';

/** Work/school: choose your OneDrive, a shared folder, or a SharePoint site's library. */
function Locations({ onOpen, onCancel }: { onOpen: (s: Start) => void; onCancel: () => void }) {
  const [data, setData] = useState<DriveLocations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [site, setSite] = useState<{ id: string; name: string } | null>(null);
  const [libraries, setLibraries] = useState<Array<{ id: string; name: string }> | null>(null);

  useEffect(() => {
    let cancelled = false;
    endpoints
      .driveLocations()
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError && e.code === 'needs_permission' ? 'Allow SharePoint access first (Settings → Account).' : 'Could not load SharePoint sites.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!site) return;
    let cancelled = false;
    setLibraries(null);
    endpoints
      .siteDrives(site.id)
      .then((r) => {
        if (cancelled) return;
        // A site with a single library opens straight into it.
        if (r.drives.length === 1) onOpen({ drive: r.drives[0]!.id, name: `${site.name} / ${r.drives[0]!.name}` });
        else setLibraries(r.drives);
      })
      .catch(() => !cancelled && setError('Could not open that site.'));
    return () => {
      cancelled = true;
    };
  }, [site, onOpen]);

  return (
    <div className="rounded-xl border border-[var(--border)] p-3">
      <div className="mb-2 flex items-center gap-2 text-sm">
        {site && (
          <button onClick={() => setSite(null)} aria-label="Back to locations" className="rounded p-1 hover:bg-[var(--border)]">
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
        )}
        <span className="truncate font-medium">{site ? site.name : 'Choose where to look'}</span>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <ul className="max-h-72 space-y-0.5 overflow-y-auto">
        {site ? (
          libraries === null ? (
            <Loading />
          ) : libraries.length === 0 ? (
            <li className="muted px-2 text-sm">No document libraries on this site.</li>
          ) : (
            libraries.map((l) => (
              <li key={l.id}>
                <button className={rowClass} onClick={() => onOpen({ drive: l.id, name: `${site.name} / ${l.name}` })}>
                  <Library className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{l.name}</span>
                </button>
              </li>
            ))
          )
        ) : (
          <>
            <li>
              <button className={rowClass} onClick={() => onOpen({ name: 'OneDrive' })}>
                <Cloud className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                <span className="min-w-0 flex-1 truncate">My OneDrive</span>
                <ChevronRight className="h-4 w-4 opacity-50" aria-hidden />
              </button>
            </li>
            {!data && !error && <Loading />}
            {data && data.shared.length > 0 && <Heading>Shared with me</Heading>}
            {data?.shared.map((f) => (
              <li key={`${f.drive}:${f.id}`}>
                <button className={rowClass} onClick={() => onOpen({ drive: f.drive, folderId: f.id, name: f.name })}>
                  <Users className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <ChevronRight className="h-4 w-4 opacity-50" aria-hidden />
                </button>
              </li>
            ))}
            {data && data.sites.length > 0 && <Heading>SharePoint sites</Heading>}
            {data?.sites.map((s) => (
              <li key={s.id}>
                <button className={rowClass} onClick={() => setSite({ id: s.id, name: s.name })}>
                  <Building2 className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{s.name}</span>
                  <ChevronRight className="h-4 w-4 opacity-50" aria-hidden />
                </button>
              </li>
            ))}
          </>
        )}
      </ul>
      <div className="mt-3 flex justify-end">
        <button className={ghostButtonClass} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const Heading = ({ children }: { children: string }) => (
  <li className="muted px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider">{children}</li>
);

const Loading = () => (
  <li className="muted flex items-center gap-2 px-2 py-1.5 text-sm">
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
  </li>
);

function Browser({
  start,
  path,
  setPath,
  onPick,
  onBack,
  onCancel,
  onDisconnected,
}: {
  start: Start;
  path: Crumb[];
  setPath: (p: Crumb[]) => void;
  onPick: (folderId: string, name: string) => void;
  /** Back from the starting folder to the list of locations (work accounts). */
  onBack?: () => void;
  onCancel: () => void;
  onDisconnected: () => void;
}) {
  const [listing, setListing] = useState<DriveListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = path[path.length - 1]!;
  // Held in a ref: the parent passes a new function each render, which must not refetch.
  const disconnected = useRef(onDisconnected);
  disconnected.current = onDisconnected;

  useEffect(() => {
    let cancelled = false;
    setListing(null);
    setError(null);
    endpoints
      .driveChildren(current.id, start.drive, current.id ? undefined : current.name)
      .then((l) => !cancelled && setListing(l))
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === 'not_connected') disconnected.current();
        setError(e instanceof Error ? e.message : 'Could not open folder');
      });
    return () => {
      cancelled = true;
    };
  }, [current.id, current.name, start.drive]);

  // A library's top level can be used too ("root"); your own OneDrive's top level can't.
  const pickId = current.id ?? (start.drive ? 'root' : undefined);
  const label = path.map((p) => p.name).join(' / ');

  return (
    <div className="rounded-xl border border-[var(--border)] p-3">
      <div className="mb-2 flex items-center gap-2 text-sm">
        {(path.length > 1 || onBack) && (
          <button
            onClick={() => (path.length > 1 ? setPath(path.slice(0, -1)) : onBack?.())}
            aria-label={path.length > 1 ? 'Up one folder' : 'Back to locations'}
            className="rounded p-1 hover:bg-[var(--border)]"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
        )}
        <span className="truncate font-medium">{label}</span>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      {!listing && !error && (
        <p className="muted flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
        </p>
      )}
      {listing && (
        <>
          <ul className="max-h-64 space-y-0.5 overflow-y-auto">
            {listing.folders.map((f) => (
              <li key={f.id}>
                <button onClick={() => setPath([...path, { id: f.id, name: f.name }])} className={rowClass}>
                  <Folder className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="muted text-xs">{f.childCount}</span>
                </button>
              </li>
            ))}
            {listing.folders.length === 0 && <li className="muted px-2 text-sm">No sub-folders.</li>}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="muted text-xs">
              {listing.mediaCount} photo{listing.mediaCount === 1 ? '' : 's'}/video{listing.mediaCount === 1 ? '' : 's'} here
              {listing.folders.length > 0 && ' + sub-folders'}
            </span>
            <div className="flex gap-2">
              <button className={ghostButtonClass} onClick={onCancel}>
                Cancel
              </button>
              <button
                className={buttonClass}
                // A folder of only sub-folders (e.g. Pictures → 2024, 2025) is fine: they're included.
                disabled={!pickId || (listing.mediaCount === 0 && listing.folders.length === 0)}
                // Folders outside your own OneDrive are labelled with where they live.
                onClick={() => pickId && onPick(pickId, start.drive ? label : current.name)}
              >
                Use this folder
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
